import React, { useState, useEffect } from "react";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { communityServicesAPI } from "../../../services/api";
import toast from "react-hot-toast";

const CreateNewVolunteer = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    type: "Resident",
    role: "",
    name: "",
    contact: "",
    program: "",
    program_id: null,
    status: "",
    address: "",
    selectedResident: null,
  });

  const [searchResults, setSearchResults] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [programOpen, setProgramOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Fetch programs and allowed values
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await communityServicesAPI.getVolunteerPrograms();
        if (response.data.success && response.data.data.length > 0) {
          setProgramOptions(response.data.data.flat());
        }
      } catch (err) {
        console.error("Error fetching programs:", err);
      }
    };
    fetchPrograms();

    setRoleOptions(["organizer", "coordinator", "volunteer"]);
    setStatusOptions(["pending", "active", "inactive"]);
  }, []);

  // Handle volunteer type change
  const handleTypeChange = (type) => {
    setFormData({
      type,
      role: "",
      name: "",
      contact: "",
      program: "",
      program_id: null,
      status: "",
      address: "",
      selectedResident: null,
    });
    setSearchResults([]);
  };

  // Handle name input for Resident search
  const handleNameInput = async (e) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      name: value,
      selectedResident:
        prev.selectedResident?.full_name === value
          ? prev.selectedResident
          : null,
      contact:
        prev.selectedResident?.full_name === value ? prev.contact : "",
    }));

    if (formData.type !== "Resident" || value.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const response = await communityServicesAPI.searchResidentVolunteer(value);
      const residents = response.data?.data?.searched_resident || [];
      setSearchResults(residents);
    } catch (err) {
      console.error("Error searching resident:", err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Select resident from search results
  const selectResident = (res) => {
    const resident_id = res.resident_id || res.id;
    setFormData((prev) => ({
      ...prev,
      name: res.full_name,
      contact: res.contact_number || res.contact_no || "",
      selectedResident: { ...res, resident_id },
    }));
    setSearchResults([]);
  };

  // Create volunteer
  // const handleCreate = async () => {
  //   // Validation
  //   if (!formData.role || !formData.status || !formData.program_id || !formData.contact) {
  //     alert("Please fill in all required fields");
  //     return;
  //   }

  //   if (formData.type === "Resident" && !formData.selectedResident) {
  //     alert("Please select a resident from the list");
  //     return;
  //   }

  //   if (formData.type === "Non-Resident" && (!formData.name || !formData.address)) {
  //     alert("Please fill in name and address for non-resident");
  //     return;
  //   }

  //   try {
  //     const payload = {
  //       is_resident: formData.type === "Resident",
  //       role: formData.role.toLowerCase(),
  //       resident_id: formData.type === "Resident" ? formData.selectedResident?.resident_id : null,
  //       volunteer_name: formData.type === "Non-Resident" ? formData.name : null,
  //       address: formData.type === "Non-Resident" ? formData.address : null,
  //       contact_information: formData.contact,
  //       program_id: formData.program_id,
  //       status: formData.status.toLowerCase(),
  //     };

  //     const response = await communityServicesAPI.createVolunteer(payload);

  //     if (response.data.success) {
  //       onCreate(response.data.data.volunteer);
  //       onClose();
  //     } else {
  //       alert(response.data.message || "Failed to create volunteer");
  //     }
  //   } catch (err) {
  //     console.error("Error creating volunteer:", err);
  //     alert("Error creating volunteer. See console for details.");
  //   }
  // };

  const handleCreate = async () => {
    // Validation
    if (!formData.role || !formData.status || !formData.program_id || !formData.contact) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.type === "Resident" && !formData.selectedResident) {
      toast.error("Please select a resident from the list");
      return;
    }

    if (formData.type === "Non-Resident" && (!formData.name || !formData.address)) {
      toast.error("Please fill in name and address for non-resident");
      return;
    }

    try {
      const payload = {
        is_resident: formData.type === "Resident",
        role: formData.role.toLowerCase(),
        resident_id: formData.type === "Resident" ? formData.selectedResident?.resident_id : null,
        volunteer_name: formData.type === "Non-Resident" ? formData.name : null,
        address: formData.type === "Non-Resident" ? formData.address : null,
        contact_information: formData.contact,
        program_id: formData.program_id,
        status: formData.status.toLowerCase(),
      };

      const response = await communityServicesAPI.createVolunteer(payload);

      if (response.data.success) {
        toast.success("Volunteer created successfully!");
        onCreate(response.data.data.volunteer);
        onClose();
      } else {
        toast.error(response.data.message || "Failed to create volunteer");
      }
    } catch (err) {
      console.error("Error creating volunteer:", err);
      toast.error("Volunteer already exists for this resident.");
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-sm w-full max-w-4xl overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-medium text-gray-800">Create New Volunteer</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-8 space-y-8">
          {/* Volunteer Type & Role */}
          <div className="grid grid-cols-2 gap-8">
            {/* Type */}
            <div className="space-y-3">
              <label className="text-[15px] font-semibold text-gray-700">Volunteer Type</label>
              <div className="flex gap-4">
                {["Resident", "Non-Resident"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => handleTypeChange(t)}
                    className={`w-full py-3 px-5 border rounded-lg flex items-center gap-3 transition-all ${formData.type === t ? "border-blue-500 ring-1 ring-blue-500" : "border-gray-200"}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.type === t ? "border-blue-500" : "border-gray-300"}`}>
                      {formData.type === t && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                    </div>
                    <span className="font-medium">{t}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Role */}
            <div className="space-y-2 relative">
              <label className="text-[15px] font-semibold text-gray-700">
                Role <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setRoleOpen(!roleOpen)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg flex justify-between items-center text-gray-400 bg-white"
              >
                <span className={formData.role ? "text-gray-600" : ""}>{formData.role || "Select a role"}</span>
                <ChevronDownIcon className="w-5 h-5 text-gray-800" />
              </button>
              {roleOpen && (
                <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                  {roleOptions.map((opt) => (
                    <li
                      key={opt}
                      onClick={() => {
                        setFormData({ ...formData, role: opt });
                        setRoleOpen(false);
                      }}
                      className="px-4 py-3 text-sm hover:bg-gray-50 cursor-pointer text-gray-700"
                    >
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Name, Contact, Program, Status */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-6">
            {/* Name */}
            <div className="relative space-y-2">
              <label className="text-[15px] font-semibold text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter volunteer name"
                value={formData.name}
                onChange={handleNameInput}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 outline-none text-gray-600 placeholder:text-gray-400"
              />
              {formData.type === "Resident" && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {isSearching ? (
                    <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
                  ) : (
                    searchResults.map((res) => (
                      <div
                        key={res.id}
                        onClick={() => selectResident(res)}
                        className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-none"
                      >
                        <p className="text-sm font-medium text-gray-800">{res.full_name}</p>
                        <p className="text-xs text-gray-500">{res.contact_number}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Contact */}
            <div className="space-y-2">
              <label className="text-[15px] font-semibold text-gray-700">
                Contact Info <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter contact info"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 outline-none text-gray-600 placeholder:text-gray-400"
              />
            </div>

            {/* Address for Non-Resident */}
            {formData.type === "Non-Resident" && (
              <div className="space-y-2 col-span-2">
                <label className="text-[15px] font-semibold text-gray-700">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:border-blue-500 outline-none text-gray-600 placeholder:text-gray-400"
                />
              </div>
            )}

            {/* Program */}
            <div className="space-y-2 relative">
              <label className="text-[15px] font-semibold text-gray-700">
                Assigned Program <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setProgramOpen(!programOpen)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg flex justify-between items-center text-gray-400 bg-white"
              >
                <span className={formData.program ? "text-gray-600" : ""}>{formData.program || "Select a program"}</span>
                <ChevronDownIcon className="w-5 h-5 text-gray-800" />
              </button>
              {programOpen && (
                <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {programOptions.length > 0 ? (
                    programOptions.map((opt) => (
                      <li
                        key={opt.value}
                        onClick={() => {
                          setFormData({ ...formData, program: opt.name, program_id: opt.value });
                          setProgramOpen(false);
                        }}
                        className="px-4 py-3 text-sm hover:bg-gray-50 cursor-pointer text-gray-700"
                      >
                        {opt.name}
                      </li>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">No programs available</div>
                  )}
                </ul>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2 relative">
              <label className="text-[15px] font-semibold text-gray-700">
                Status <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setStatusOpen(!statusOpen)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg flex justify-between items-center text-gray-400 bg-white"
              >
                <span className={formData.status ? "text-gray-600" : ""}>{formData.status || "Select a status"}</span>
                <ChevronDownIcon className="w-5 h-5 text-gray-800" />
              </button>
              {statusOpen && (
                <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                  {statusOptions.map((opt) => (
                    <li
                      key={opt}
                      onClick={() => {
                        setFormData({ ...formData, status: opt });
                        setStatusOpen(false);
                      }}
                      className="px-4 py-3 text-sm hover:bg-gray-50 cursor-pointer text-gray-700"
                    >
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-4 px-8 py-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-10 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            className="px-10 py-2.5 bg-[#2563eb] text-white rounded-lg hover:bg-blue-700"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateNewVolunteer;
